import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { IconCheckCircleFilled } from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { NeedHelpSection } from '../../NeedHelpSection/NeedHelpSection';
import { ContinueButton } from '../ContinueButton/ContinueButton';
import { RepaymentCalculatorButton } from '../RepaymentCalculatorButton/RepaymentCalculatorButton';
import {
    FullTermDurationRow,
    FullTermRepaymentRow,
    InterestOnlyRepaymentRows,
    InterestRateRow,
    WithdrawalAmountRow,
    WithdrawalFeesRow
} from '../SummaryRow/SummaryRow';

export const DesktopLoanSummary = ({
    selectedOffer,
    drawdownAmount,
    monthlyRepayment,
    isLoading,
    isSingleOffer
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    monthlyRepayment: number | null;
    isLoading: boolean;
    isSingleOffer: boolean;
}) => {
    const isInterestOnly = selectedOffer
        ? getIsInterestOnlyOffer(selectedOffer)
        : false;

    return (
        <div className="relative grow basis-0 bg-primary-10 text-white">
            {/* Offset by navbar height (72px) */}
            <div className="sticky bottom-0 top-header-offset flex min-h-[calc(100dvh-var(--header-offset))] max-w-[472px] flex-col p-2xl pt-5xl">
                {selectedOffer?.personal_guarantee_proportion === 0 && (
                    <div className="mb-m flex items-center gap-s rounded-xs border border-success-drk bg-success-lt px-m py-s">
                        <IconCheckCircleFilled className="h-l w-l shrink-0 text-success-drk" />
                        <Text className="font-bold">
                            <FormattedMessage
                                id="_.loan_summary.no_personal_guarantee"
                                defaultMessage="No Personal Guarantee Required"
                            />
                        </Text>
                    </div>
                )}

                <Text className="mb-s text-xl font-bold text-structure-100 min-sm:text-2xl">
                    {isSingleOffer && selectedOffer ? (
                        <FormattedMessage
                            id="_.loan_summary.single_offer_title_desktop_v21"
                            defaultMessage="Your repayments over {months} months"
                            values={{
                                months: getDurationInMonths(
                                    selectedOffer.duration!
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.loan_summary.title_v21"
                            defaultMessage="Your loan summary"
                        />
                    )}
                </Text>

                {isInterestOnly ? (
                    <div className="flex flex-col gap-m">
                        <InterestOnlyRepaymentRows
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    </div>
                ) : (
                    <>
                        <Text className="text-m font-reg text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment"
                                defaultMessage="Min. monthly repayment"
                            />
                        </Text>
                        <Text className="text-m font-med text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment_desktop"
                                defaultMessage="{amount} per month"
                                values={{
                                    amount: (
                                        <span className="text-3xl font-bold">
                                            {monthlyRepayment !== null ? (
                                                <FormattedCurrency
                                                    value={monthlyRepayment}
                                                />
                                            ) : (
                                                '£--.--'
                                            )}
                                        </span>
                                    )
                                }}
                            />
                        </Text>
                    </>
                )}

                <V21SummaryFields
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                    isLoading={isLoading}
                />

                <div className="mt-xl">
                    <ContinueButton
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                </div>

                <div className="mt-5xl">
                    <NeedHelpSection theme="dark" />
                </div>
            </div>
        </div>
    );
};

type SummaryFieldsProps = {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    isLoading: boolean;
};

const V21SummaryFields = ({
    selectedOffer,
    drawdownAmount,
    isLoading
}: SummaryFieldsProps) => (
    <>
        <div
            className={`mt-l flex flex-col gap-m border-t border-secondary-20 pt-l ${isLoading ? 'opacity-50' : ''}`}
        >
            <WithdrawalAmountRow drawdownAmount={drawdownAmount} />
            <InterestRateRow
                selectedOffer={selectedOffer}
                drawdownAmount={drawdownAmount}
            />
            <WithdrawalFeesRow selectedOffer={selectedOffer} />
        </div>

        <RepaymentCalculatorButton
            selectedOffer={selectedOffer}
            drawdownAmount={drawdownAmount}
        />

        <div className={`flex flex-col gap-m ${isLoading ? 'opacity-50' : ''}`}>
            <FullTermDurationRow
                selectedOffer={selectedOffer}
                drawdownAmount={drawdownAmount}
            />
            <FullTermRepaymentRow
                selectedOffer={selectedOffer}
                drawdownAmount={drawdownAmount}
            />
        </div>
    </>
);
